import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ReceiptText, RefreshCw, ChevronDown, Gift, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import api, { formatPrice } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Loader } from "@/components/Loader";

const STATUS = {
  paid: { label: "Paid", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  payment_processing: { label: "Payment processing", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  payment_pending: { label: "Pending", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  payment_failed: { label: "Payment unsuccessful", cls: "bg-rose-100 text-rose-700 border-rose-200" },
  payment_error: { label: "Failed", cls: "bg-rose-100 text-rose-700 border-rose-200" },
};

const fmtDate = (s) => {
  try {
    return new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch (e) {
    return "";
  }
};

const fmtDelivery = (s) => {
  try {
    return new Date(s + "T00:00:00").toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
  } catch (e) {
    return s;
  }
};

export default function Orders() {
  const navigate = useNavigate();
  const { reorder } = useCart();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    api
      .get("/orders")
      .then((r) => setOrders(r.data.orders || []))
      .catch(() => toast.error("Could not load orders."))
      .finally(() => setLoading(false));
  }, []);

  const doReorder = (o) => {
    reorder(o.items);
    toast.success("Items added to your cart! 🛒");
    navigate("/cart");
  };

  if (loading) return <Loader label="Fetching your orders…" />;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display font-bold text-3xl sm:text-4xl text-slate-800 tracking-tight flex items-center gap-3">
          <ReceiptText className="text-emerald-500" /> Order History
        </h1>
        <p className="text-slate-500 mt-1">Reorder past lunches with one tap.</p>
      </div>

      {orders.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-3xl border-2 border-dashed border-emerald-200">
          <div className="text-5xl mb-3">🧾</div>
          <p className="text-slate-500 font-medium">No orders yet. Time for lunch!</p>
          <button
            data-testid="orders-browse-menu"
            onClick={() => navigate("/menu")}
            className="mt-5 inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-display font-bold rounded-2xl px-6 py-3 shadow-md shadow-emerald-200"
          >
            <ShoppingCart size={18} /> Browse Menu
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((o) => {
            const st = STATUS[o.status] || STATUS.payment_pending;
            const open = expanded === o.order_id;
            const addonTotal = Math.max(0, (o.total_cents || 0) - (o.subtotal_cents || 0) + (o.discount_cents || 0));
            return (
              <div key={o.order_id} data-testid={`order-${o.order_id}`} className="bg-white rounded-3xl border-2 border-emerald-100/80 overflow-hidden">
                <div className="p-5 flex flex-wrap items-center gap-4">
                  <div className="flex-1 min-w-[180px]">
                    <div className="flex items-center gap-2.5">
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold border ${st.cls}`}>{st.label}</span>
                      <span className="text-sm text-slate-500">{fmtDate(o.created_at)}</span>
                    </div>
                    <p className="font-display font-semibold text-slate-800 mt-1.5">
                      {o.items?.length} item{o.items?.length !== 1 ? "s" : ""} · <span className="text-emerald-600">{formatPrice(o.total_cents)}</span>
                    </p>
                  </div>

                  <button
                    data-testid={`order-toggle-${o.order_id}`}
                    onClick={() => setExpanded(open ? null : o.order_id)}
                    className="inline-flex items-center gap-1 font-semibold text-slate-500 hover:text-emerald-600 text-sm"
                  >
                    Details <ChevronDown size={16} className={`transition-transform ${open ? "rotate-180" : ""}`} />
                  </button>
                  <button
                    data-testid={`reorder-${o.order_id}`}
                    onClick={() => doReorder(o)}
                    className="inline-flex items-center gap-2 bg-rose-400 hover:bg-rose-500 text-white font-bold text-sm rounded-2xl px-4 py-2.5 shadow-md shadow-rose-200 transition-all active:scale-95"
                  >
                    <RefreshCw size={15} /> Reorder
                  </button>
                </div>

                {open && (
                  <div className="border-t-2 border-emerald-50 p-5 bg-emerald-50/40 space-y-3">
                    {(o.child_name || o.school || o.delivery_date) && (
                      <div className="text-sm text-slate-600 space-y-0.5" data-testid={`order-meta-${o.order_id}`}>
                        {o.child_name && <p><span className="font-semibold text-slate-800">Child:</span> {o.child_name}</p>}
                        {o.school && <p><span className="font-semibold text-slate-800">School:</span> {o.school}</p>}
                        {o.delivery_date && <p><span className="font-semibold text-slate-800">Delivery date:</span> {fmtDelivery(o.delivery_date)}</p>}
                      </div>
                    )}
                    <div className="space-y-2">
                      {o.items?.map((it, idx) => (
                        <div key={idx}>
                          <div className="flex items-center gap-3">
                            <img src={it.image_url} alt="" className="h-11 w-11 rounded-xl object-cover" />
                            <div className="flex-1">
                              <p className="font-semibold text-slate-800 text-sm">{it.name}</p>
                              {it.config && (
                                <p className="text-xs text-slate-500">{Object.values(it.config).filter(Boolean).join(" · ")}</p>
                              )}
                            </div>
                            <span className="text-sm text-slate-600 font-medium">
                              {it.quantity} × {formatPrice(it.unit_amount)}
                            </span>
                          </div>
                          {Array.isArray(it.addons) && it.addons.length > 0 && (
                            <ul className="mt-1 pl-14 space-y-0.5" data-testid={`order-item-addons-${idx}`}>
                              {it.addons.map((a, ai) => (
                                <li key={ai} className="text-xs text-emerald-700">+ {a}</li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="h-px bg-emerald-100 my-2" />
                    <div className="flex justify-between text-sm text-slate-600">
                      <span>Meal subtotal</span>
                      <span>{formatPrice(o.subtotal_cents)}</span>
                    </div>
                    {addonTotal > 0 && (
                      <div className="flex justify-between text-sm text-slate-600" data-testid={`order-addon-total-${o.order_id}`}>
                        <span>Add-ons</span>
                        <span>+{formatPrice(addonTotal)}</span>
                      </div>
                    )}
                    {o.discount_cents > 0 && (
                      <div className="flex justify-between text-sm text-emerald-600 font-semibold">
                        <span className="inline-flex items-center gap-1"><Gift size={14} /> Free meal ({o.free_count})</span>
                        <span>−{formatPrice(o.discount_cents)}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-display font-bold text-slate-800">
                      <span>Total</span>
                      <span className="text-emerald-600">{formatPrice(o.total_cents)}</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
